import React from 'react';
import { HelpCircle, ChevronDown, Calculator, ListChecks } from 'lucide-react';

interface FaqItem {
  question: string;
  answer: string;
  category: 'checklist' | 'budget';
}

const faqs: FaqItem[] = [
  {
    category: 'checklist',
    question: "Qu'est-ce que la checklist PVT ?",
    answer: "La checklist PVT est un outil interactif con\u00e7u pour vous aider \u00e0 organiser toutes les \u00e9tapes de votre d\u00e9part et installation \u00e0 l'\u00e9tranger en PVT. Elle vous permet de suivre vos d\u00e9marches avant le d\u00e9part, \u00e0 l'arriv\u00e9e et au retour."
  },
  {
    category: 'checklist',
    question: "Comment utiliser la checklist ?",
    answer: "C'est simple ! S\u00e9lectionnez votre destination, cochez les t\u00e2ches compl\u00e9t\u00e9es, ajoutez vos propres sous-t\u00e2ches et suivez votre progression en temps r\u00e9el."
  },
  {
    category: 'checklist',
    question: "Puis-je personnaliser ma checklist ?",
    answer: "Oui ! Vous pouvez ajouter des sous-t\u00e2ches, modifier leur priorit\u00e9 et mettre des notes pour vous organiser au mieux."
  },
  {
    category: 'checklist',
    question: "La checklist est-elle accessible hors ligne ?",
    answer: "Non, la checklist n\u00e9cessite une connexion internet pour \u00eatre accessible. Cependant, vous pouvez ajouter la web app \u00e0 l'\u00e9cran d'accueil de votre t\u00e9l\u00e9phone pour un acc\u00e8s rapide lorsque vous \u00eates en ligne."
  },
  {
    category: 'checklist',
    question: "Puis-je partager ma checklist avec quelqu'un ?",
    answer: "Pas encore, mais nous travaillons sur une fonctionnalit\u00e9 de partage."
  },
  {
    category: 'budget',
    question: "Comment fonctionne le simulateur de budget ?",
    answer: "Notre simulateur vous permet d'estimer vos d\u00e9penses avant et pendant votre PVT. Il prend en compte votre ville d'arriv\u00e9e, le type de logement, les d\u00e9penses mensuelles et bien plus encore."
  },
  {
    category: 'budget',
    question: "D'o\u00f9 viennent les donn\u00e9es utilis\u00e9es dans le simulateur ?",
    answer: "Les montants affich\u00e9s sont des estimations bas\u00e9es sur l'API Numbeo, reconnue pour sa fiabilit\u00e9. Les co\u00fbts r\u00e9els peuvent varier en fonction de votre situation, de la p\u00e9riode et de la zone g\u00e9ographique."
  },
  {
    category: 'budget',
    question: "Comment sont g\u00e9r\u00e9es les conversions de devises ?",
    answer: "Les conversions sont effectu\u00e9es en temps r\u00e9el en utilisant le taux de change actuel pour vous donner une estimation au plus proche de la r\u00e9alit\u00e9."
  },
  {
    category: 'budget',
    question: "Puis-je enregistrer mon budget et y revenir plus tard ?",
    answer: "Oui, vous pouvez tout \u00e0 fait enregistrer jusqu'\u00e0 4 budgets diff\u00e9rents, ce qui vous permet de les comparer \u00e0 tout moment."
  },
  {
    category: 'budget',
    question: "Comment puis-je ajuster mon budget en fonction de mes d\u00e9penses r\u00e9elles ?",
    answer: "Vous pouvez modifier les montants sugg\u00e9r\u00e9s et ajouter des d\u00e9penses suppl\u00e9mentaires pour personnaliser votre budget selon vos besoins."
  }
];

export function FaqSection() {
  const [activeCategory, setActiveCategory] = React.useState<'checklist' | 'budget'>('checklist');
  const [openQuestions, setOpenQuestions] = React.useState<Set<string>>(new Set());

  const toggleQuestion = (question: string) => {
    setOpenQuestions(prev => {
      const next = new Set(prev);
      if (next.has(question)) {
        next.delete(question);
      } else {
        next.add(question);
      }
      return next;
    });
  };

  const filteredFaqs = faqs.filter(faq => faq.category === activeCategory);

  return (
    <section className="py-16 sm:py-24 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 text-sm font-medium mb-4">
            <HelpCircle className="w-4 h-4" />
            <span>FAQ</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Questions fr&eacute;quentes
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Tout ce que vous devez savoir pour pr&eacute;parer votre PVT sereinement.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setActiveCategory('checklist')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeCategory === 'checklist'
                  ? 'bg-primary-500 text-white shadow-md shadow-primary-500/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <ListChecks className="w-4 h-4" />
              <span>Checklist PVT</span>
            </button>
            <button
              onClick={() => setActiveCategory('budget')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeCategory === 'budget'
                  ? 'bg-primary-500 text-white shadow-md shadow-primary-500/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Calculator className="w-4 h-4" />
              <span>Simulateur de Budget</span>
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {filteredFaqs.map((faq, index) => {
            const isOpen = openQuestions.has(faq.question);
            return (
              <div
                key={index}
                className={`bg-white dark:bg-gray-800 rounded-2xl border transition-all duration-300 overflow-hidden ${
                  isOpen
                    ? 'border-primary-200 dark:border-primary-800/50 shadow-lg shadow-primary-500/5'
                    : 'border-gray-100 dark:border-gray-700/50 hover:border-gray-200 dark:hover:border-gray-600'
                }`}
              >
                <button
                  onClick={() => toggleQuestion(faq.question)}
                  className="w-full text-left px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-4"
                >
                  <span className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                    {faq.question}
                  </span>
                  <div className={`p-1.5 rounded-lg transition-all duration-300 flex-shrink-0 ${
                    isOpen ? 'bg-primary-100 dark:bg-primary-900/30 rotate-180' : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <ChevronDown className={`w-4 h-4 transition-colors ${
                      isOpen ? 'text-primary-500' : 'text-gray-400'
                    }`} />
                  </div>
                </button>

                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-5 sm:px-6 pb-5">
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
